import React from 'react';
import { CreditCard, DollarSign, RefreshCw } from 'lucide-react';
import { useCreditsPage } from './useCreditsPage';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';

const CreditsPage: React.FC = () => {
  const {
    navigate,
    balance,
    balanceLoading,
    transactionsLoading,
    transactions,
    error,
    paginatedTransactions,
    sortedTransactions,
    totalPages,
    startIndex,
    endIndex,
    currentPage,
    handleRefresh,
    formatTransactionType,
    formatTransactionAmount,
    getTransactionIcon,
    getSortIcon,
    handleSort,
    handlePageChange,
  } = useCreditsPage();

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-32 sm:-right-64 -top-32 sm:-top-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"></div>
        <div className="absolute -left-32 sm:-left-64 -bottom-32 sm:-bottom-64 w-[400px] sm:w-[600px] lg:w-[800px] h-[400px] sm:h-[600px] lg:h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"></div>
      </div>
      <div className="relative mx-auto px-2 sm:px-3 lg:px-8 py-3 sm:py-4 lg:py-6 xl:py-8">
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div>
              <div className="flex items-center space-x-2 sm:space-x-3 mb-1 sm:mb-2">
                <div className="p-1.5 sm:p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg shadow-lg">
                  <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-white" />
                </div>
                <h1 className="text-lg sm:text-xl lg:text-3xl xl:text-4xl font-bold text-slate-50">
                  Credit Management
                </h1>
              </div>
              <p className="text-gray-300 text-xs sm:text-sm lg:text-lg">
                Manage your credits (minutes) and view transaction history
              </p>
            </div>
            <button
              onClick={() => { track(AnalyticsEvents.CREDITS_PURCHASE_CLICK); navigate('/subscription'); }}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-2 sm:py-2.5 lg:py-3 px-3 sm:px-4 lg:px-6 rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-colors flex items-center justify-center touch-manipulation"
            >
              <DollarSign className="h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 mr-1 sm:mr-2" />
              <span className="text-xs sm:text-sm lg:text-base">Purchase Credits</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/20 border border-red-400 text-red-100 px-4 py-3 rounded-lg">{error}</div>
        )}

        <div className="mb-4 sm:mb-6">
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/70 backdrop-blur-xl rounded-2xl border border-slate-600/50 p-4 sm:p-5 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base lg:text-lg font-semibold text-white">Credit Balance</h2>
              <button onClick={handleRefresh} className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-all" title="Refresh balance">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3 sm:gap-4">
              <div className="group relative text-center rounded-2xl overflow-hidden border border-emerald-400/40 bg-gradient-to-br from-emerald-500/20 via-slate-800/90 to-slate-900 transition-all duration-300 hover:border-emerald-400/60 hover:-translate-y-0.5 card-glow-emerald card-glow-hover-emerald">
                <div className="h-1.5 w-full bg-gradient-to-r from-emerald-400 via-green-400 to-teal-500" />
                <div className="p-4 sm:p-5">
                  {balanceLoading ? (
                    <div className="animate-pulse space-y-2">
                      <div className="h-8 w-12 bg-slate-600 rounded-lg mx-auto" />
                      <div className="h-3 w-16 bg-slate-600 rounded mx-auto" />
                      <div className="h-3 w-20 bg-slate-600 rounded mx-auto hidden sm:block" />
                    </div>
                  ) : (
                    <>
                      <p className="text-2xl sm:text-3xl font-extrabold text-emerald-300 tabular-nums text-glow-emerald">{balance?.available_credits || 0}</p>
                      <p className="text-xs font-bold text-emerald-400/90 mt-1 uppercase tracking-wider">Available</p>
                      <p className="text-xs text-slate-500 mt-0.5 hidden sm:block">Ready to use</p>
                    </>
                  )}
                </div>
              </div>
              <div className="group relative text-center rounded-2xl overflow-hidden border border-cyan-400/40 bg-gradient-to-br from-cyan-500/20 via-slate-800/90 to-slate-900 transition-all duration-300 hover:border-cyan-400/60 hover:-translate-y-0.5 card-glow-cyan card-glow-hover-cyan">
                <div className="h-1.5 w-full bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-500" />
                <div className="p-4 sm:p-5">
                  {balanceLoading ? (
                    <div className="animate-pulse space-y-2">
                      <div className="h-8 w-12 bg-slate-600 rounded-lg mx-auto" />
                      <div className="h-3 w-16 bg-slate-600 rounded mx-auto" />
                      <div className="h-3 w-20 bg-slate-600 rounded mx-auto hidden sm:block" />
                    </div>
                  ) : (
                    <>
                      <p className="text-2xl sm:text-3xl font-extrabold text-cyan-300 tabular-nums text-glow-cyan">{balance?.total_purchased || 0}</p>
                      <p className="text-xs font-bold text-cyan-400/90 mt-1 uppercase tracking-wider">Purchased</p>
                      <p className="text-xs text-slate-500 mt-0.5 hidden sm:block">All time</p>
                    </>
                  )}
                </div>
              </div>
              <div className="group relative text-center rounded-2xl overflow-hidden border border-amber-400/40 bg-gradient-to-br from-amber-500/20 via-slate-800/90 to-slate-900 transition-all duration-300 hover:border-amber-400/60 hover:-translate-y-0.5 card-glow-amber">
                <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-orange-400 to-rose-500" />
                <div className="p-4 sm:p-5">
                  {balanceLoading ? (
                    <div className="animate-pulse space-y-2">
                      <div className="h-8 w-12 bg-slate-600 rounded-lg mx-auto" />
                      <div className="h-3 w-16 bg-slate-600 rounded mx-auto" />
                      <div className="h-3 w-20 bg-slate-600 rounded mx-auto hidden sm:block" />
                    </div>
                  ) : (
                    <>
                      <p className="text-2xl sm:text-3xl font-extrabold text-amber-300 tabular-nums text-glow-amber">{balance?.total_consumed || 0}</p>
                      <p className="text-xs font-bold text-amber-400/90 mt-1 uppercase tracking-wider">Used</p>
                      <p className="text-xs text-slate-500 mt-0.5 hidden sm:block">All time</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-lg sm:rounded-xl shadow-2xl border border-slate-700/50">
          <div className="px-2 sm:px-3 lg:px-6 py-2 sm:py-3 lg:py-4 border-b border-slate-700/30">
            <div>
              <h2 className="text-sm sm:text-lg lg:text-xl font-semibold text-white">Transaction History</h2>
              <p className="text-gray-300 text-xs sm:text-sm mt-1">View all your credit transactions</p>
            </div>
          </div>
          <div className="relative">
            {transactionsLoading && transactions.length === 0 ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto mb-4"></div>
                <p className="text-slate-300">Loading transactions...</p>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No transactions yet</h3>
                <p className="text-gray-300 mb-6">Your credit transactions will appear here</p>
                <button onClick={() => { track(AnalyticsEvents.CREDITS_PURCHASE_CLICK); navigate('/subscription'); }} className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-md hover:from-cyan-600 hover:to-blue-700 transition-colors">
                  Purchase Credits
                </button>
              </div>
            ) : (
              <>
                {transactionsLoading && transactions.length > 0 && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-blue-600 animate-pulse z-10"></div>
                )}
                <div className="block sm:hidden space-y-2 p-2 sm:p-3">
                  {paginatedTransactions.map((transaction) => (
                    <div key={transaction.id} className="bg-gradient-to-br from-slate-800/20 to-slate-900/30 rounded-lg p-3 border border-slate-700/30 hover:bg-slate-800/30 transition-all duration-300">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center min-w-0 flex-1">
                          <div className="flex-shrink-0 mr-2">{getTransactionIcon(transaction)}</div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-medium text-white truncate">{formatTransactionType(transaction)}</div>
                            <div className="text-xs text-gray-400">{new Date(transaction.created_at).toLocaleDateString()}</div>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <div className={`text-xs font-semibold ${transaction.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>{formatTransactionAmount(transaction.amount)}</div>
                          <div className="text-xs text-gray-400">Bal: {transaction.balance_after}</div>
                        </div>
                      </div>
                      <div className="text-xs text-slate-300 bg-slate-800/30 rounded p-1.5">{transaction.description}</div>
                    </div>
                  ))}
                </div>
                <div className="hidden sm:block overflow-x-auto scrollbar-hide">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('type')}>
                          <div className="flex items-center space-x-1"><span>Type</span>{getSortIcon('type')}</div>
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('description')}>
                          <div className="flex items-center space-x-1"><span>Description</span>{getSortIcon('description')}</div>
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('amount')}>
                          <div className="flex items-center justify-end space-x-1"><span>Amount</span>{getSortIcon('amount')}</div>
                        </th>
                        <th className="hidden md:table-cell px-6 py-4 text-right text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('balance_after')}>
                          <div className="flex items-center justify-end space-x-1"><span>Balance</span>{getSortIcon('balance_after')}</div>
                        </th>
                        <th className="hidden lg:table-cell px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors" onClick={() => handleSort('created_at')}>
                          <div className="flex items-center space-x-1"><span>Date</span>{getSortIcon('created_at')}</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/30">
                      {paginatedTransactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-slate-800/30 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">{getTransactionIcon(transaction)}</div>
                              <div className="ml-3 min-w-0">
                                <div className="text-sm font-medium text-white truncate">{formatTransactionType(transaction)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4"><div className="text-sm text-gray-300 max-w-xs truncate">{transaction.description}</div></td>
                          <td className="px-6 py-4 text-right">
                            <div className={`text-sm font-semibold ${transaction.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>{formatTransactionAmount(transaction.amount)}</div>
                          </td>
                          <td className="hidden md:table-cell px-6 py-4 text-right"><div className="text-sm text-gray-300">{transaction.balance_after}</div></td>
                          <td className="hidden lg:table-cell px-6 py-4">
                            <div className="text-sm text-gray-300">{new Date(transaction.created_at).toLocaleDateString()}</div>
                            <div className="text-xs text-gray-400">{new Date(transaction.created_at).toLocaleTimeString()}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <div className="px-2 sm:px-3 lg:px-6 py-2 sm:py-3 lg:py-4 pb-4 mb-1 border-t border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2 sm:gap-4">
              <div className="text-xs text-gray-300 text-center">
                Showing {startIndex + 1} to {Math.min(endIndex, sortedTransactions.length)} of {sortedTransactions.length} transactions
              </div>
              {totalPages > 1 ? (
                <div className="flex items-center justify-center space-x-1">
                  <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-2 sm:px-3 py-1 text-xs bg-gradient-to-r from-slate-800/30 to-slate-900/40 text-white border border-slate-700/50 rounded-md hover:from-slate-700/40 hover:to-slate-800/50 hover:border-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg touch-manipulation">
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 3) pageNum = i + 1;
                      else if (currentPage <= 2) pageNum = i + 1;
                      else if (currentPage >= totalPages - 1) pageNum = totalPages - 2 + i;
                      else pageNum = currentPage - 1 + i;
                      return (
                        <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`px-2 sm:px-3 py-1 text-xs rounded-md transition-colors touch-manipulation ${currentPage === pageNum ? 'bg-cyan-500 text-white' : 'bg-gradient-to-r from-slate-800/30 to-slate-900/40 text-slate-300 hover:from-slate-700/40 hover:to-slate-800/50'}`}>
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-2 sm:px-3 py-1 text-xs bg-gradient-to-r from-slate-800/30 to-slate-900/40 text-white border border-slate-700/50 rounded-md hover:from-slate-700/40 hover:to-slate-800/50 hover:border-slate-600/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-lg touch-manipulation">
                    Next
                  </button>
                </div>
              ) : (
                <div className="text-xs text-gray-400 text-center">Page 1 of 1</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditsPage;
